import type { VoucherDTO } from "~/lib/api";

function mainServiceCodes(voucher: VoucherDTO): string[] | null {
  if (voucher.Category === "PACKAGE") return null;
  if (voucher.OnlyIntegration === true) return null;
  const codes = voucher.ServicesIncludedCodes || [];
  return codes.length > 0 ? codes : null;
}

/**
 * The service codes covered by every entrance or treatment voucher in
 * `vouchers`, or `null` when there is none (only packages, upselling vouchers
 * and vouchers valid on any service).
 */
export function commonMainServiceCodes(vouchers: VoucherDTO[]): string[] | null {
  let common: string[] | null = null;
  for (const voucher of vouchers) {
    const codes = mainServiceCodes(voucher);
    if (!codes) continue;
    common = common === null ? [...codes] : common.filter((code) => codes.includes(code));
  }
  return common;
}

/**
 * `true` when `incoming` can be redeemed together with `existing`.
 *
 * The booking flow books one main service for every guest and applies only
 * the vouchers that include it, so entrance and treatment vouchers must cover
 * at least one common service. The category check does not see this: a Daily
 * voucher (`ENTRANCE_DAY_SPA_*`) and a 3-hour one (`ENTRANCE_3H_*`) share the
 * same category, both were accepted, and the second one was never used
 * (CHLSPA-049). This is the rule the redeem page already states ("Non puoi
 * riscattare voucher diversi che contengono ingressi"): different entrances
 * are booked separately, while several vouchers for the same entrance (one
 * per guest, or a carnet) still go together.
 *
 * Packages (matched on their own), upselling vouchers (spent on the upselling
 * step) and vouchers with no services listed (valid on any service) never
 * conflict here.
 */
export function canCombineMainServiceVoucher(
  existing: VoucherDTO[],
  incoming: VoucherDTO,
): boolean {
  if (!mainServiceCodes(incoming)) return true;
  const common = commonMainServiceCodes([...existing, incoming]);
  return common === null || common.length > 0;
}

/**
 * `services` without the entrances and treatments that some voucher in
 * `vouchers` does not cover.
 *
 * The service step offered every service of any voucher: with a weekday-only
 * Daily and an any-day one, a weekend date offered the weekend entrance and
 * the weekday voucher went unused, as in CHLSPA-049. Now that date offers
 * nothing, as it already did for the weekday voucher alone. Packages are left
 * to their own rules. A cart whose vouchers share nothing (accepted before the
 * check above existed) keeps the old list rather than an empty one.
 */
export function servicesCoveredByAllVouchers<
  T extends { Code: string; Type?: string },
>(services: T[], vouchers: VoucherDTO[]): T[] {
  const common = commonMainServiceCodes(vouchers);
  if (!common || common.length === 0) return services;
  return services.filter((s) => s.Type === "Package" || common.includes(s.Code));
}
